import { i18n } from "~vue/i18n";
import type {
    DecoratedPerformanceReview,
    PerformanceMomentCta,
    PerformanceReviewConversationListItem,
    PerformanceReviewRelationship,
    PerformanceReviewResponse,
} from "~vue/types/performance_management";
import { computed } from "vue";

/** Join reviews with their CTA, relationship and conversation records. Shared by the PM page and the in-chat picker. */
export const decorateReviews = (
    reviews: PerformanceReviewResponse[],
    ctas: PerformanceMomentCta[],
    relationships: PerformanceReviewRelationship[],
    conversations: PerformanceReviewConversationListItem[],
): DecoratedPerformanceReview[] =>
    reviews.map((review) => ({
        ...review,
        cta: ctas.find((c) => c.id === review.performance_moment_cta_id) || null,
        relationship: review.performance_review_relationship_id === null ? null : relationships.find((r) => r.id === review.performance_review_relationship_id) || null,
        conversation: conversations.find((c) => c.performance_review_id === review.id) || null,
    }));

/** Display labels only; branch on `getReviewGroupStatusState`, since these change with the locale. */
export const STATUS = computed(() => ({
    NOT_STARTED: i18n.global.t("moments.people.status.notStarted"),
    IN_PROGRESS: i18n.global.t("moments.people.status.inProgress"),
    COMPLETE: i18n.global.t("moments.people.status.complete"),
}));

export const isReviewCompleted = (review: DecoratedPerformanceReview): boolean => {
    return !!review.manually_completed_at || review.conversation?.state === "completed";
};

const isReviewInProgress = (review: DecoratedPerformanceReview): boolean => !isReviewCompleted(review) && review.conversation?.state === "in_progress";

export const getReviewGroupStatus = (reviews: DecoratedPerformanceReview[]): string => {
    const completed = reviews.filter(isReviewCompleted).length;
    const total = reviews.length;
    if (completed === 0) {
        return reviews.some(isReviewInProgress) ? STATUS.value.IN_PROGRESS : STATUS.value.NOT_STARTED;
    }
    if (completed === total) {
        return STATUS.value.COMPLETE;
    }
    return i18n.global.t("moments.people.status.partiallyComplete", { completed, total });
};

export const getReviewGroupStatusState = (reviews: DecoratedPerformanceReview[]) => {
    const completed = reviews.filter(isReviewCompleted).length;
    if (completed === 0 && !reviews.some(isReviewInProgress)) {
        return "neutral";
    }
    if (completed === reviews.length) {
        return "done";
    }
    return "active";
};
